import constants from "../../../../appConstants";
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter,
} from '@dataGrid/components/columnFilters';
import { DropdownEditor, NumericEditor } from '@dataGrid/components/cellEditors';
import {
  DropdownValueCellRenderer,
  StopStatusCellRenderer,
  SpecialNeedsValueGetter,
  SpecialNeedsRenderer,
  DotRenderer
} from '@dataGrid/components/cellRenderer/index';
import {
  mergeUserConfig,
  formatDeliveryWindow,
  formatDateTime,
  suppressEnter,
  formatTime,
  suppressKeyboardEventHandler,
} from '@dataGrid/gridUtilities';
import { isPickupStop, getCustomerName } from '../Common/MergeStops/utills';
import EtaEtdRenderer from './EtaEtdRenderer';
import CustomerNameCell from '../../../common/CustomerMergeIndicator/CustomerNameCell';

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }

  static enableRowDrag = (params) => {
    return (
      params.api &&
      params.api.selectedRouteNameList &&
      params.api.selectedRouteNameList.length === 1 &&
      params.api.getDisplayedRowCount() > 1 &&
      params.data.stopType !== constants.ROUTE_STOP_TYPES.AP
    );
  };

  static routeCellRenderer = (params) => {
    return params.value && params.value.label
      ? params.value.label
      : params.value;
  };

  static isRouteNameEditable = (params) => {
    return !params.data.isLocked && params.data.stopType !==constants.ROUTE_STOP_TYPES.AP;
  };

  static getConfiguration({
    userConfig,
    featureFlags,
    ROUTENAME_EDITOR_PARAMS,
    shouldShowTwoManInTrailerInventory,
    enableNoCargoVanCustomer,
    enableFixedEquipmentSize,
    enableRevenueFieldsOnUI
  }) {
    const defaultConfig = GridConfig.getDefaultConfiguration({
      featureFlags,
      ROUTENAME_EDITOR_PARAMS,
      shouldShowTwoManInTrailerInventory,
      enableNoCargoVanCustomer,
      enableFixedEquipmentSize,
      enableRevenueFieldsOnUI
    });
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    });
  }

  static customerNameRenderer(params) {
    const { data } = params;
    if (isPickupStop(data.stopType)) {
      return data.shipperName;
    }
    return (
      <CustomerNameCell displayCustomerName={data.displayCustomerName} customerName={data.consigneeName} />
    );
  }


  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getCustomerAddress(params) {
    return isPickupStop(params.data.stopType)
      ? [params.data.shipperAddress, params.data.shipperAddress2].filter(Boolean).join(', ')
      : [params.data.consigneeAddress, params.data.consigneeAddress2].filter(Boolean).join(', ');
  }

  static getCustomerCity(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperCity
      : params.data.city;
  }

  static getCustomerState(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperState
      : params.data.state;
  }

  static getCustomerZip(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperZip
      : params.data.zip;
  }

  static getCustomerCode(params) {
    return isPickupStop(params.data.stopType)
      ? params.data.shipperCode
      : params.data.displayCustomerCode;
  }

  static getFormattedAPTWindow(params) {
    return formatDeliveryWindow({ ...params, value: params.data.deliveryWindow });
  }

  static getDefaultConfiguration({ 
    featureFlags, 
    ROUTENAME_EDITOR_PARAMS, 
    shouldShowTwoManInTrailerInventory,
    enableNoCargoVanCustomer,
    enableFixedEquipmentSize,
    enableRevenueFieldsOnUI
  }) {
    
    const defaultConfig = {
      title: constants.gridNames.StopsGrid,
      keyColumn: "routeStopID",
      rowSelection: "multiple",
      rowDeselection: "true",
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        cellDataType: false,
        suppressKeyboardEvent: suppressKeyboardEventHandler(),
      },
      columns: [
        {
          headerName: "",
          minWidth: constants.columnWidth.VIOLATION_DOT,
          maxWidth: constants.columnWidth.VIOLATION_DOT,
          field: "violation",
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: ["gridCell-centerAlign", "gridCell-dot"],
          filter: false,
          cellRenderer: DotRenderer,
          isInvisibleInShowHide: true,
          gridTitle: constants.gridNames.StopsGrid,
        },
        {
          headerName: "Seq",
          field: "stopSequence",
          headerTooltip: "Stop Sequence",
          minWidth: constants.columnWidth.STOP_SEQUENCE,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: "gridCell-leftAlign",
          rowDrag: GridConfig.enableRowDrag,
          filter: MultiSelectFilter,
          headerClass: "stopSequenceLabelAlign",
        },
        {
          headerName: "Route",
          field: "routeName",
          headerTooltip: "Route Name",
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          minWidth:constants.columnWidth.ROUTES,
          editable: GridConfig.isRouteNameEditable,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          cellEditor: DropdownEditor,
          cellEditorPopup: true,
          cellEditorParams: {
            canAcceptNewValue: true,
            ...ROUTENAME_EDITOR_PARAMS,
          },
          cellRenderer: DropdownValueCellRenderer,
          suppressKeyboardEvent: suppressKeyboardEventHandler(suppressEnter),
          suppressSizeToFit:true,
        },
        {
          headerName: "Ldg Zone",
          field: "loadingZone",
          headerTooltip: "Loading Zone",
          minWidth: constants.columnWidth.LDG_ZONE,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Customer",
          field: "displayCustomerName",
          headerTooltip: "Customer Name",
          minWidth: constants.columnWidth.CONSIGNEE_NAME,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          valueGetter : (params) => getCustomerName(params.data),
          cellRenderer: GridConfig.customerNameRenderer,
          filterParams:{
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: "Spl Needs",
          headerTooltip: "Special Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          cellClass: "gridCell-leftAlign",
          valueGetter: SpecialNeedsValueGetter,
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          cellRenderer: SpecialNeedsRenderer,
          cellRendererParams: {
            getAPTWindow: GridConfig.getFormattedAPTWindow,
          },
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
        },
        {
          headerName: "Max IB ETA",
          headerTooltip: "Shipment ETA",
          field: "stopIbeta",
          minWidth: constants.columnWidth.IBETA,
          cellClass: "gridCell-leftAlign",
          valueFormatter: formatDateTime,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        },
        {
          headerName: "Address",
          field: "consigneeAddress",
          headerTooltip: "Customer Address",
          minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerAddress,
          tooltipValueGetter: (params) => params.value,
          filterParams:{
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: "City",
          headerTooltip: "City",
          field: "city",
          cellClass: "gridCell-leftAlign",
          minWidth: constants.columnWidth.CITY,
          filter: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerCity,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "State",
          headerTooltip: "State",
          field: "state",
          cellClass: "gridCell-leftAlign",
          minWidth: constants.columnWidth.STATE,
          filter: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerState,
        },
        {
          headerName: "Zip",
          field: "zip",
          headerTooltip: "Zip",
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerZip,
        },
        {
          headerName: "Bills",
          field: "bills",
          headerTooltip: "Bills",
          minWidth: constants.columnWidth.BILLS,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "HU",
          headerTooltip: "Handling Unit",
          field: "totalHu",
          minWidth: constants.columnWidth.HU,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "PSE",
          field: "pse",
          minWidth: constants.columnWidth.PSE,
          //editable: GridConfig.isEditable,
          headerTooltip: "Pallet Space Equivalent",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          //tooltipField: "pse"
          hide: featureFlags.IsWeightBasedRoutingEnabled,
        },
        {
          headerName: "Calculated PSE",
          field: "calculatedPse",
          minWidth: constants.columnWidth.CALCULATED_PSE,
          headerTooltip: "Calculated Pallet Space Equivalent",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          hide: true,
        },
        {
          headerName: "Weight",
          field: "totalWeight",
          headerTooltip: "Weight",
          minWidth: constants.columnWidth.WEIGHT,
          cellClass: "gridCell-centerAlign",
          filter: MultiSelectFilter,
        },
        // {
        //   headerName: "Service",
        //   field: "priority",
        //   headerTooltip: "Service",
        //   minWidth: constants.columnWidth.PRIORITY,
        //   cellClass: "gridCell-leftAlign",
        //   filter: MultiSelectFilter
        // },
        // {
        //   headerName: "LG",
        //   headerTooltip: "Lift Gate",
        //   field: "liftgate",
        //   minWidth: constants.columnWidth.LG,
        //   cellClass: "gridCell-leftAlign",
        //   filter: MultiSelectFilter
        // },
        // {
        //   headerName: "Equip Pref",
        //   field: "eqptPref",
        //   headerTooltip: "Equipment Preference",
        //   minWidth: constants.columnWidth.EQUIPMENT_PREFERENCE,
        //   cellClass: "gridCell-leftAlign",
        //   filter: MultiSelectFilter
        // },
        {
          headerName: "Delivery Window",
          field: "deliveryWindow",
          headerTooltip: "Delivery Window",
          minWidth: constants.columnWidth.DELIVERY_WINDOW,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
          editable: false,
          valueFormatter: formatDeliveryWindow,
        },
        {
          headerName: "ETA",
          field: "eta",
          headerTooltip: "ETA",
          minWidth: constants.columnWidth.ETA,
          cellClass: GridConfig.timeViolation,
          cellClass: "gridCell-leftAlign",
          cellRenderer: (props) => <EtaEtdRenderer params={props} fieldName="eta" />,
          filter: DateTimeFilter,
          valueFormatter: formatTime,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
        },
        {
          headerName: "ETD",
          field: "etd",
          headerTooltip: "ETD",
          minWidth: constants.columnWidth.ETD,
          cellClass: "gridCell-leftAlign",
          cellRenderer: (props) => <EtaEtdRenderer params={props} fieldName="etd" />,
          valueFormatter: formatTime,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
        },
        {
          headerName: "Status",
          field: "stopStatus",
          headerTooltip: "Stop Status",
          minWidth: constants.columnWidth.STOP_STATUS,
          cellClassRules: {
            violation: (params) =>
              params.value === constants.STOP_STATUS.DELAYED
          },
          filter: MultiSelectFilter,
          valueGetter: StopStatusCellRenderer,
        },
        {
          headerName: "Stop Time (Mins)",
          field: "stopTime",
          headerTooltip: "Stop Time",
          editable: featureFlags.IsStopTimeEditable,
          minWidth: constants.columnWidth.STOP_TIME,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          cellEditor: NumericEditor,
          cellEditorPopup: true,
        },
        {
          headerName: "Rem Duty",
          field: "dutyTime",
          headerTooltip: "Remaining Duty Time",
          minWidth: constants.columnWidth.W_90,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          hide: true
        },
        {
          headerName: "Cubic Feet",
          headerTooltip: "Cubic Feet",
          field: "cubesCount",
          minWidth: constants.columnWidth.CUBIC_FEET,
          cellClass: "gridCell-centerAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Customer#",
          headerTooltip: "Customer Account Number",
          field: "displayCustomerCode",
          minWidth: constants.columnWidth.CUSTOMER_CODE,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          valueGetter : GridConfig.getCustomerCode,
          tooltipValueGetter: (params) => params.value,
          hide: true,
        },
        {
          headerName: "Latitude",
          field: "latitude",
          minWidth: constants.columnWidth.LATITUDE,
          //editable: GridConfig.isEditable,
          headerTooltip: "Latitude",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          tooltipField: "latitude",
          hide: true,
        },
        {
          headerName: "Longitude",
          field: "longitude",
          minWidth: constants.columnWidth.LONGITUDE,
          //editable: GridConfig.isEditable,
          headerTooltip: "Longitude",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          tooltipField: "longitude",
          hide: true,
        },
        // {
        //   headerName: "Special Instructions",
        //   field: "specialInstruction",
        //   minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
        //   headerTooltip : "Special Instructions",
        //   cellClass: "gridCell-leftAlign",
        //   filter: MultiSelectFilter,
        //   tooltipField: "specialInstruction"
        // },
        {
          headerName: "Pieces",
          headerTooltip: "Pieces",
          field: "totalPieces",
          minWidth: constants.columnWidth.PIECES,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Pallets",
          headerTooltip: "Pallets",
          field: "totalPallets",
          minWidth: constants.columnWidth.PIECES,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Min Trailer",
          headerTooltip: "Min Feasible Trailer Length",
          field: "minEquipmentSize",
          editable: false,
          minWidth: constants.columnWidth.MAX_TRAILER,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Max Trailer",
          headerTooltip: "Max Feasible Trailer Length",
          field: "maxFeasibleTrailerLength",
          editable: false,
          minWidth: constants.columnWidth.MAX_TRAILER,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: 'Fixed Trailer',
          headerTooltip: 'Fixed Trailer Length',
          field: 'fixedEquipmentSize',
          minWidth: constants.columnWidth.FIXED_TRAILER,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
        },
        {
          headerName: "Distance From PrevStop",
          headerTooltip: "Distance From Previous Stop",
          field: "distanceFromPrevStop",
          minWidth: constants.columnWidth.W_200,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Stop Type",
          headerTooltip: "Stop Type",
          hide : true,
          field: "stopType",
          minWidth: constants.columnWidth.MAX_TRAILER,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Cargo Van",
          headerTooltip: "Cargo Van Eligible",
          field: "isCargoVanEligible",
          minWidth: constants.columnWidth.W_120,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          hide : true,
        },
        {
          headerName: "2 Man",
          headerTooltip: "Two Man Required",
          field: "isTwoManRequired",
          minWidth: constants.columnWidth.W_100,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          hide : true,
        },
        {
          headerName: "Revenue",
          headerTooltip: "Revenue",
          field: "totalRevenue",
          minWidth: constants.columnWidth.W_100,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        }
      ],
    }
    defaultConfig.columns = defaultConfig.columns.filter(
      (column) => {
        switch (column.field) {
          case "isTwoManRequired": return shouldShowTwoManInTrailerInventory;
          case "isCargoVanEligible": return enableNoCargoVanCustomer;
          case "fixedEquipmentSize": return enableFixedEquipmentSize;
          case "totalRevenue": return enableRevenueFieldsOnUI;
          default: return true
        }
      }
    );
    return defaultConfig;
  }

}
